"use client";

import { useTheme } from "next-themes";
import type { ForwardedRef, HTMLAttributes, ReactElement } from "react";
import { forwardRef, memo, useCallback, useMemo } from "react";
import {
  oneDark,
  oneLight,
} from "react-syntax-highlighter/dist/esm/styles/prism";
import { Virtuoso } from "react-virtuoso";
import { cn } from "@/lib/utils";
import SyntaxHighlighter from "./json-syntax-highlighter";

type VirtualizedCodeBlockProps = HTMLAttributes<HTMLDivElement> & {
  lines: string[];
  language?: string;
  maxHeight?: number;
  minHeight?: number;
  lineHeight?: number;
};

function ScrollerComponent(
  { className, ...props }: HTMLAttributes<HTMLDivElement>,
  ref: ForwardedRef<HTMLDivElement>,
): ReactElement {
  return (
    <div ref={ref} className={cn("overflow-auto", className)} {...props} />
  );
}

const Scroller = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ScrollerComponent,
);
Scroller.displayName = "VirtualizedCodeBlockScroller";

function VirtualizedCodeBlockComponent({
  lines,
  language = "json",
  className,
  maxHeight = 360,
  minHeight = 120,
  lineHeight = 20,
  ...props
}: VirtualizedCodeBlockProps): ReactElement {
  const { resolvedTheme } = useTheme();
  const theme = resolvedTheme === "dark" ? oneDark : oneLight;

  const height = useMemo(() => {
    const contentHeight = lines.length * lineHeight + 32;
    return Math.min(maxHeight, Math.max(minHeight, contentHeight));
  }, [lines.length, lineHeight, maxHeight, minHeight]);

  const renderLine = useCallback(
    (index: number, line: string) => {
      const isFirst = index === 0;
      const isLast = index === lines.length - 1;
      return (
        <div className={cn("px-4", isFirst && "pt-4", isLast && "pb-4")}>
          <SyntaxHighlighter
            CodeTag="span"
            PreTag="div"
            className="whitespace-pre"
            codeTagProps={{
              className: "font-mono text-xs",
              style: { lineHeight: `${lineHeight}px` },
            }}
            customStyle={{
              margin: 0,
              padding: 0,
              background: "transparent",
            }}
            language={language}
            style={theme}
            useInlineStyles
          >
            {line === "" ? " " : line}
          </SyntaxHighlighter>
        </div>
      );
    },
    [language, lineHeight, lines.length, theme],
  );

  return (
    <div
      className={cn(
        "relative w-full overflow-hidden rounded-md border bg-background text-foreground",
        className,
      )}
      {...props}
    >
      <Virtuoso
        components={{ Scroller }}
        data={lines}
        itemContent={renderLine}
        style={{ height }}
      />
    </div>
  );
}

export const VirtualizedCodeBlock = memo(
  VirtualizedCodeBlockComponent,
  (prev, next) =>
    prev.className === next.className &&
    prev.lines === next.lines &&
    prev.language === next.language &&
    prev.maxHeight === next.maxHeight &&
    prev.minHeight === next.minHeight &&
    prev.lineHeight === next.lineHeight,
);
